/**
 * firestoreSync.js
 *
 * Syncs local data to Firestore so supervisors can see all agents' work.
 * Local-first: agent always writes to localStorage first, then syncs to
 * Firestore when online. Offline writes are queued and replayed on online.
 *
 * Schema (nested multi-tenant):
 *   organizations/{orgId}/notes/{noteId}
 *   organizations/{orgId}/sessions/{sessionId}
 *   organizations/{orgId}/individuals/{individualId}
 *   users/{userId}    ← stays top-level for auth-tied profile lookup
 */

import {
  setDoc, updateDoc, deleteDoc, getDocs, addDoc,
  query, where, orderBy, onSnapshot, serverTimestamp, limit, doc,
} from "firebase/firestore";
import { db, auth } from "./firebase";
import {
  currentOrgId,
  cacheOrgId,
  orgCollection,
  orgDoc,
} from "./orgPaths";

// Re-export for backward compatibility with existing callers.
export function cacheUserOrg(org) {
  cacheOrgId(org);
}

// ===== HELPERS =====

function getCurrentUser() {
  const user = auth.currentUser;
  if (!user) return null;
  return {
    uid: user.uid,
    email: user.email,
    displayName: user.displayName || user.email,
  };
}

// ===== SYNC: NOTES =====

/**
 * Sync a note to organizations/{orgId}/notes/{noteId}.
 * Called after addNote() or updateNote() in useLocalData.
 */
export async function syncNoteToFirestore(noteEntry) {
  try {
    const user = getCurrentUser();
    if (!user) {
      queueOfflineWrite("note", noteEntry);
      return;
    }

    const noteId = noteEntry.id || `note_${Date.now()}`;
    const noteRef = orgDoc("notes", noteId);

    await setDoc(noteRef, {
      ...noteEntry,
      // Strip oversized transcript before send. Local copy stays full-fidelity.
      transcript: noteEntry.transcript ? noteEntry.transcript.substring(0, 50000) : null,
      agentId: user.uid,
      agentName: user.displayName,
      agentEmail: user.email,
      syncedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      supervisorReviewed: noteEntry.supervisorReviewed || false,
      supervisorSignedBy: noteEntry.supervisorSignedBy || null,
      safetyReviewed: noteEntry.safetyReviewed || false,
    }, { merge: true });

    removeFromOfflineQueue("note", noteId);
    return { success: true, id: noteId };
  } catch (e) {
    console.error("Failed to sync note to Firestore:", e);
    queueOfflineWrite("note", noteEntry);
    return { success: false, error: e.message };
  }
}

export async function syncNoteTrashToFirestore(noteId) {
  try {
    const noteRef = orgDoc("notes", noteId);
    await updateDoc(noteRef, {
      status: "trashed",
      trashedAt: serverTimestamp(),
    });
  } catch (e) {
    console.error("Failed to sync note trash:", e);
  }
}

// ===== SYNC: COMPLETED SESSIONS =====

export async function syncSessionToFirestore(session) {
  try {
    const user = getCurrentUser();
    if (!user) {
      queueOfflineWrite("session", session);
      return;
    }

    const sessionId = session.id || `sess_${Date.now()}`;
    const sessRef = orgDoc("sessions", sessionId);

    await setDoc(sessRef, {
      ...session,
      agentId: user.uid,
      agentName: user.displayName,
      agentEmail: user.email,
      syncedAt: serverTimestamp(),
    });

    removeFromOfflineQueue("session", sessionId);
  } catch (e) {
    console.error("Failed to sync session to Firestore:", e);
    queueOfflineWrite("session", session);
  }
}

// ===== SYNC: INDIVIDUALS =====

export async function syncIndividualToFirestore(individual) {
  try {
    const user = getCurrentUser();
    if (!user) {
      queueOfflineWrite("individual", individual);
      return;
    }

    const indId = individual.id;
    const indRef = orgDoc("individuals", indId);

    await setDoc(indRef, {
      ...individual,
      agentId: user.uid,
      agentName: user.displayName,
      syncedAt: serverTimestamp(),
    }, { merge: true });

    removeFromOfflineQueue("individual", indId);
  } catch (e) {
    console.error("Failed to sync individual:", e);
    queueOfflineWrite("individual", individual);
  }
}

export async function syncIndividualDeleteToFirestore(individualId) {
  try {
    const indRef = orgDoc("individuals", individualId);
    await updateDoc(indRef, {
      status: "deleted",
      deletedAt: serverTimestamp(),
    });
  } catch (e) {
    console.error("Failed to sync individual delete:", e);
  }
}

// ===== AGENT STATUS =====

export async function updateAgentLiveStatus(status, currentIndividual = null) {
  try {
    const user = getCurrentUser();
    if (!user) return;

    // Top-level users/ stays unchanged.
    const userRef = doc(db, "users", user.uid);
    await updateDoc(userRef, {
      liveStatus: status,
      currentIndividual: currentIndividual || null,
      lastStatusUpdate: serverTimestamp(),
    });
  } catch (e) {
    console.error("Failed to update agent status:", e);
  }
}

// ===== FIRESTORE LISTENERS (admin dashboard, etc.) =====

/**
 * Subscribe to all notes in an organization.
 * organizationId argument retained for backward compatibility with callers,
 * but the path now scopes the query so no `where('organization', ...)` clause
 * is needed.
 */
export function onOrgNotes(organizationId, callback) {
  const q = query(
    orgCollection("notes", organizationId),
    orderBy("updatedAt", "desc"),
    limit(200)
  );
  return onSnapshot(q, (snapshot) => {
    const notes = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
    callback(notes);
  }, (error) => {
    console.error("Org notes listener error:", error);
    callback([]);
  });
}

export function onAgentNotes(agentId, organizationId, callback) {
  const q = query(
    orgCollection("notes", organizationId),
    where("agentId", "==", agentId),
    orderBy("updatedAt", "desc"),
    limit(200)
  );
  return onSnapshot(q, (snapshot) => {
    const notes = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
    callback(notes);
  }, (error) => {
    console.error("Agent notes listener error:", error);
    callback([]);
  });
}

export function onOrgSessions(organizationId, callback) {
  const q = query(
    orgCollection("sessions", organizationId),
    orderBy("syncedAt", "desc"),
    limit(500)
  );
  return onSnapshot(q, (snapshot) => {
    const sessions = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
    callback(sessions);
  }, (error) => {
    console.error("Org sessions listener error:", error);
    callback([]);
  });
}

export function onAgentSessions(agentId, organizationId, callback) {
  const q = query(
    orgCollection("sessions", organizationId),
    where("agentId", "==", agentId),
    orderBy("syncedAt", "desc"),
    limit(500)
  );
  return onSnapshot(q, (snapshot) => {
    const sessions = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
    callback(sessions);
  }, (error) => {
    console.error("Agent sessions listener error:", error);
    callback([]);
  });
}

export function onOrgIndividuals(organizationId, callback) {
  // Note: "status != deleted" filter requires an inequality query; with the
  // nested path we still want to exclude soft-deleted rows client-side.
  const q = query(orgCollection("individuals", organizationId));
  return onSnapshot(q, (snapshot) => {
    const individuals = snapshot.docs
      .map(d => ({ id: d.id, ...d.data() }))
      .filter(i => i.status !== "deleted");
    callback(individuals);
  }, (error) => {
    console.error("Org individuals listener error:", error);
    callback([]);
  });
}

/**
 * Team members of the org. Source of truth is organizations/{orgId}/members,
 * with displayName/email/role denormalised from the top-level users/{uid} doc.
 */
export function onOrgTeam(organizationId, callback) {
  const q = query(orgCollection("members", organizationId));
  return onSnapshot(q, (snapshot) => {
    const members = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
    callback(members);
  }, (error) => {
    console.error("Org team listener error:", error);
    callback([]);
  });
}

// ===== OFFLINE QUEUE =====

const OFFLINE_QUEUE_KEY = "fieldnotes_sync_queue";

function getOfflineQueue() {
  try {
    return JSON.parse(localStorage.getItem(OFFLINE_QUEUE_KEY) || "[]");
  } catch {
    return [];
  }
}

function saveOfflineQueue(queue) {
  localStorage.setItem(OFFLINE_QUEUE_KEY, JSON.stringify(queue));
}

function queueOfflineWrite(type, data) {
  const queue = getOfflineQueue();
  const id = data.id || `${type}_${Date.now()}`;
  if (!queue.find(q => q.id === id && q.type === type)) {
    queue.push({ type, id, data, queuedAt: new Date().toISOString() });
    saveOfflineQueue(queue);
  }
}

function removeFromOfflineQueue(type, id) {
  const queue = getOfflineQueue().filter(q => !(q.id === id && q.type === type));
  saveOfflineQueue(queue);
}

export async function syncOfflineQueue() {
  const queue = getOfflineQueue();
  if (queue.length === 0) return;

  console.log(`[Sync] Processing ${queue.length} queued writes...`);

  for (const item of queue) {
    try {
      if (item.type === "note") {
        await syncNoteToFirestore(item.data);
      } else if (item.type === "session") {
        await syncSessionToFirestore(item.data);
      } else if (item.type === "individual") {
        await syncIndividualToFirestore(item.data);
      }
    } catch (e) {
      console.error(`[Sync] Failed to process queued ${item.type}:`, e);
    }
  }
}

// ===== BULK INITIAL SYNC =====

export async function syncAllLocalData(localData) {
  const user = getCurrentUser();
  if (!user) return;

  console.log("[Sync] Starting bulk sync of local data...");

  const notes = localData.notes || [];
  for (const note of notes) {
    await syncNoteToFirestore(note);
  }

  const sessions = localData.completedSessions || [];
  for (const session of sessions) {
    await syncSessionToFirestore(session);
  }

  const individuals = localData.individuals || [];
  for (const ind of individuals) {
    await syncIndividualToFirestore(ind);
  }

  console.log(`[Sync] Bulk sync complete: ${notes.length} notes, ${sessions.length} sessions, ${individuals.length} individuals`);
}
